import React, { createContext, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import usePage from '@dashboard/hooks/usePage';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import { pageKey } from '@dashboard/lib/queryKeys';
import { getFieldPresets } from '@dashboard/services/pageFieldTypes';

// The page's own form unless a provider says otherwise, as Guest questions does. Guest questions
// have layout blocks but not the page form's discount code or data types (hasLayout).
const FieldSourceContext = createContext(null);

const PAGE_SETTINGS = {
  hasLayout: true,
  layoutBlocks: true,
  allowedElements: null,
  isLocked: () => false,
  noun: 'field',
};

export function FieldSourceProvider({ source, children }) {
  return <FieldSourceContext.Provider value={source}>{children}</FieldSourceContext.Provider>;
}

/** How the editor behaves; reading it needs no page, auth or query client. */
export function useFieldSourceSettings() {
  return useContext(FieldSourceContext) ?? PAGE_SETTINGS;
}

export function useFieldSource() {
  const custom = useContext(FieldSourceContext);
  const { page } = usePage();
  const queries = usePageQueries();
  const queryClient = useQueryClient();

  if (custom) return custom;
  return {
    ...PAGE_SETTINGS,
    fields: page?.fields,
    presets: getFieldPresets(page?.type),
    saveField: (values, fieldId) => queries.saveField(values, fieldId, page._id),
    deleteField: fieldId => queries.deleteField(fieldId, page._id),
    refresh: () => queryClient.invalidateQueries({ queryKey: pageKey(page._id) }),
  };
}

export function useSourceFields() {
  const custom = useContext(FieldSourceContext);
  const { page } = usePage();
  return custom ? custom.fields : page?.fields;
}
